import React, { useState, useEffect } from 'react';
import {
  FormControl,
  FormHelperText,
  Box,
  Typography,
  MenuItem,
  Select,
  Stack,
  Divider,
  InputLabel,
} from '@mui/material';
import { FieldTypeEnum, FieldOption } from 'views/App/Portal/PerformanceReporting/FieldBank/_types';
import { TemplateField, SectionFieldOption } from '../../_types';
import { DirectFieldInput } from 'views/App/Portal/PerformanceReporting/_components/FieldInputs';
import SingleSelectOptionManager from 'views/App/Portal/PerformanceReporting/_components/SingleSelectOptionManager';
import dayjs, { Dayjs } from 'dayjs';
import { useTemplateBuilderContext } from '../context';
import useTranslations from 'hooks/useTranslations';

interface DefaultValuePickerProps {
  field: TemplateField;
  value: string;
  onChange: (value: string) => void;
  onOptionsChange?: (options: SectionFieldOption[]) => void;
  trackUserActivity?: () => void;
  disabled?: boolean;
}

const DefaultValuePicker: React.FC<DefaultValuePickerProps> = ({
  field,
  value,
  onChange,
  onOptionsChange,
  trackUserActivity,
  disabled = false,
}) => {
  const t = useTranslations();

  // Get validation errors from context
  const { fieldOptionValidationErrors } = useTemplateBuilderContext();

  // Use fieldType from template field (backend will provide correct fieldType in sectionFields)
  const fieldType = field.fieldType || 'Text';

  // Helper function to check if an option is valid for default value selection
  const isValidOption = (opt: SectionFieldOption): boolean => {
    // Must be active
    if (opt.status !== 'Active') return false;

    // Must have a non-empty effective label
    if (!opt.effectiveLabel || opt.effectiveLabel.trim() === '') return false;

    if (opt.effectiveIsDeleted) return false;

    return true;
  };

  // Initialize field options from the field data
  const [fieldOptions, setFieldOptions] = useState<SectionFieldOption[]>(() => {
    // Use section field options from template field
    const existingOptions = field.sectionFieldOptions || [];

    if (existingOptions.length > 0) {
      return existingOptions.map((opt: SectionFieldOption) => ({
        ...opt,
        // Ensure status is set - default to Active if not specified
        status: opt.status || ('Active' as 'Active' | 'Inactive'),
      }));
    }

    // For SingleSelect fields with no existing options, create 2 empty ones
    if (fieldType === 'SingleSelect') {
      return [
        {
          label: '',
          effectiveLabel: '',
          status: 'Active' as 'Active' | 'Inactive',
          isDeleted: false,
          effectiveIsDeleted: false,
          parentSectionFieldOptionId: null,
        },
        {
          label: '',
          effectiveLabel: '',
          status: 'Active' as 'Active' | 'Inactive',
          isDeleted: false,
          effectiveIsDeleted: false,
          parentSectionFieldOptionId: null,
        },
      ];
    }

    // For other field types, return empty array
    return [];
  });

  // Update local state when field changes
  useEffect(() => {
    const existingOptions = field.sectionFieldOptions || [];

    if (existingOptions.length > 0) {
      setFieldOptions(
        existingOptions.map((opt: SectionFieldOption) => ({
          ...opt,
          status: opt.status || ('Active' as 'Active' | 'Inactive'),
        }))
      );
    } else {
      // For SingleSelect fields with no existing options, create 2 empty ones
      if (fieldType === 'SingleSelect') {
        setFieldOptions([
          {
            label: '',
            effectiveLabel: '',
            status: 'Active' as 'Active' | 'Inactive',
            isDeleted: false,
            effectiveIsDeleted: false,
            parentSectionFieldOptionId: null,
          },
          {
            label: '',
            effectiveLabel: '',
            status: 'Active' as 'Active' | 'Inactive',
            isDeleted: false,
            effectiveIsDeleted: false,
            parentSectionFieldOptionId: null,
          },
        ]);
      } else {
        // For other field types, clear options
        setFieldOptions([]);
      }
    }
  }, [field.sectionFieldOptions, field.id, fieldType]);

  // Check if current default value is still valid when field options change
  useEffect(() => {
    if (fieldType === 'SingleSelect' && value && value.trim() !== '') {
      // Don't clear inherited default values - the effective value comes from the parent
      // and the corresponding option may not be in the child's local options list
      const isInheritedDefault = field.parentSectionFieldId !== null && field.defaultValue === null;
      if (isInheritedDefault) return;

      const currentOptions = field.sectionFieldOptions || [];

      const validLabels = currentOptions
        .filter((opt) => {
          // Must be active
          if (opt.status !== 'Active') return false;
          // Must have a non-empty effective label
          if (!opt.effectiveLabel || opt.effectiveLabel.trim() === '') return false;
          // Must not be deleted
          if (opt.isDeleted || opt.effectiveIsDeleted) return false;
          return true;
        })
        .map((opt) => opt.effectiveLabel);

      if (!validLabels.includes(value)) {
        onChange('');
      }
    }
  }, [field.sectionFieldOptions, value, fieldType, onChange, field.parentSectionFieldId, field.defaultValue]);

  // Common text field styles
  const textFieldStyles = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'background.paper',
      fontSize: '0.875rem',
    },
  };

  // Handle value conversion for Date/Time fields
  const getFieldValue = () => {
    if (fieldType === 'Date' || fieldType === 'Time') {
      // Convert string value to Dayjs for the DirectFieldInput component
      if (value && value !== '') {
        return dayjs(value);
      }
      return null;
    }
    return value;
  };

  const handleFieldChange = (newValue: any) => {
    // Track user activity for text input changes
    if (trackUserActivity && (fieldType === 'Text' || fieldType === 'Number' || fieldType === 'Lookup')) {
      trackUserActivity();
    }

    if (fieldType === 'Date' || fieldType === 'Time') {
      if (newValue && dayjs.isDayjs(newValue)) {
        // Handle Dayjs objects
        if (fieldType === 'Date') {
          onChange(newValue.format('YYYY-MM-DD'));
        } else if (fieldType === 'Time') {
          onChange(newValue.toISOString());
        }
      } else if (typeof newValue === 'string' && newValue.trim() !== '') {
        // Handle string values (from BaseDateInput)
        onChange(newValue);
      } else {
        // Handle null, undefined, or empty values
        onChange('');
      }
    } else {
      // For other field types, pass the value as-is
      onChange(newValue);
    }
  };

  const renderSingleSelectInput = () => {
    // Get visible options for default value dropdown
    const visibleOptions = fieldOptions.filter(isValidOption);

    // Convert SectionFieldOption[] to FieldOption[] for SingleSelectOptionManager
    const fieldOptionsForManager: FieldOption[] = fieldOptions.map((opt) => ({
      ...opt,
      label: opt.effectiveLabel || '', // Use effectiveLabel as label (never null)
      isVisible: opt.isDeleted === null ? true : !opt.isDeleted,
    }));

    const handleOptionsChange = (newOptions: FieldOption[]) => {
      // Convert FieldOption to SectionFieldOption
      // Preserve parentSectionFieldOptionId from original options to maintain inheritance relationships
      const sectionFieldOptions: SectionFieldOption[] = newOptions.map((opt) => {
        // Find the original option to preserve parentSectionFieldOptionId and other properties
        const originalOption = fieldOptions.find(
          (original) =>
            // Match by id if both have non-zero ids (saved options)
            (original.id && opt.id && original.id === opt.id && original.id !== 0) ||
            // Match by fieldOptionId if both have it (inherited options from field bank)
            (original.fieldOptionId && opt.fieldOptionId && original.fieldOptionId === opt.fieldOptionId)
        );

        return {
          ...opt,
          id: opt.id ?? 0, // Ensure new options have id: 0, not null (backend requires Int32)
          fieldOptionId: opt.fieldOptionId ?? null, // Ensure consistency
          label: opt.label,
          effectiveLabel: opt.label,
          // Convert isVisible to isDeleted (inverted logic)
          // User edits are explicit, so convert to false (not deleted) or true (deleted), not null
          isDeleted: opt.isVisible === false, // false = deleted, true/undefined = not deleted
          effectiveIsDeleted: opt.isVisible === false,
          // Preserve parentSectionFieldOptionId from original option to maintain inheritance
          // Only set to null for truly new options (no matching original)
          parentSectionFieldOptionId: originalOption?.parentSectionFieldOptionId ?? null,
          // Preserve rank from original if it exists, otherwise use the index
          rank: originalOption?.rank ?? opt.rank ?? 0,
        };
      });

      setFieldOptions(sectionFieldOptions);
      onOptionsChange?.(sectionFieldOptions);

      // If the current default value is no longer valid, clear it
      // But don't clear inherited default values
      const isInheritedDefault = field.parentSectionFieldId !== null && field.defaultValue === null;
      if (!isInheritedDefault && value && value.trim() !== '') {
        const validLabels = sectionFieldOptions.filter(isValidOption).map((opt) => opt.effectiveLabel);

        if (!validLabels.includes(value)) {
          onChange('');
        }
      }
    };

    return (
      <Stack spacing={2}>
        {/* Options Management using shared component - only show if not disabled */}
        {!disabled && (
          <>
            <SingleSelectOptionManager
              options={fieldOptionsForManager}
              onChange={handleOptionsChange}
              mode='templateBuilder'
              trackUserActivity={trackUserActivity}
              validationError={fieldOptionValidationErrors[field.id] || ''}
            />

            {/* Divider */}
            <Divider />
          </>
        )}

        {/* Default Value Selection */}
        <FormControl fullWidth>
          <InputLabel id='default-value-label'>
            {t('performanceReporting.templates.builder.fieldProperties.defaultValue.label')}
          </InputLabel>
          <Select
            labelId='default-value-label'
            displayEmpty
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            label={t('performanceReporting.templates.builder.fieldProperties.defaultValue.label')}
            disabled={disabled}
            sx={{
              '& .MuiSelect-select': {
                fontWeight: 500,
              },
            }}
          >
            <MenuItem
              value=''
              sx={{ fontWeight: 600 }}
            >
              <span>{t('performanceReporting.templates.builder.fieldProperties.defaultValue.chooseOption')}</span>
            </MenuItem>
            {visibleOptions.map((option, index) => (
              <MenuItem
                key={index}
                value={option.effectiveLabel}
                sx={{ fontWeight: 500 }}
              >
                {option.effectiveLabel}
              </MenuItem>
            ))}
          </Select>
          {visibleOptions.length === 0 && (
            <FormHelperText>
              {t('performanceReporting.templates.builder.fieldProperties.defaultValue.noOptionsHelper')}
            </FormHelperText>
          )}
        </FormControl>
      </Stack>
    );
  };

  // For SingleSelect, use the existing complex logic
  if (fieldType === 'SingleSelect') {
    return renderSingleSelectInput();
  }

  // For all other field types, use the shared DirectFieldInput component
  return (
    <DirectFieldInput
      fieldType={fieldType}
      value={getFieldValue()}
      onChange={handleFieldChange}
      size='small'
      fullWidth={true}
      hideLabel={true}
      disabled={disabled}
    />
  );
};

export default DefaultValuePicker;
